import { Button, Close, Modal, Plus } from '../../../modules/design-system';
import type { ShippingAddress } from '../../../modules/api';
import { SelectableAddressRow } from './SelectableAddressRow';

export interface AddressPickerModalProps {
  addresses: ShippingAddress[];
  /** Id of the currently selected address (highlighted row). */
  selectedId: number | null;
  /** Pick a saved address — the page also persists it as the default. */
  onSelect: (address: ShippingAddress) => void;
  /** "Add New Address" → opens the create form. */
  onAddNew: () => void;
  onClose: () => void;
}

/**
 * Saved-address picker (web parity: "Select address" dialog). A single-select
 * list of the player's saved addresses on a light card to match the create
 * `AddressModal` it opens into. Picking a row selects it and makes it the
 * default; "Add New Address" hands off to the create form.
 */
export function AddressPickerModal({
  addresses,
  selectedId,
  onSelect,
  onAddNew,
  onClose,
}: AddressPickerModalProps) {
  return (
    <Modal onClose={onClose} inspectName="AddressPickerModal" ariaLabelledBy="address-picker-title">
      <div
        data-co-component="AddressPickerModal"
        className="relative flex max-h-[88vh] flex-col rounded-[28px] p-6"
        style={{
          background: 'var(--co-AddressPickerModal-cardFill, #FFFFFF)',
          color: 'var(--co-AddressPickerModal-textColor, #15181E)',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full transition-opacity hover:opacity-80 active:scale-95"
          style={{
            background: 'var(--co-AddressPickerModal-closeFill, rgba(0,0,0,0.05))',
            color: 'var(--co-AddressPickerModal-closeIconColor, #15181E)',
          }}
        >
          <Close size={18} strokeWidth={2} />
        </button>

        <h2 id="address-picker-title" className="mb-5 text-[20px] font-bold tracking-[-0.3px]">
          Select address
        </h2>

        <div className="flex flex-col gap-3 overflow-y-auto pr-0.5">
          {addresses.map((address) => (
            <SelectableAddressRow
              key={address.id}
              address={address}
              selected={address.id === selectedId}
              onSelect={() => onSelect(address)}
            />
          ))}
        </div>

        <div className="mt-5">
          <Button
            variant="primary"
            size="lg"
            onClick={onAddNew}
            fullWidth
            rightIcon={<Plus size={18} strokeWidth={2.4} />}
          >
            Add New Address
          </Button>
        </div>
      </div>
    </Modal>
  );
}
